import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core"
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import * as Sentry from "@sentry/react"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Id } from "@stoneflow/backend/convex/_generated/dataModel"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { Building2, GripVertical, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { RouteError } from "#/components/route-error"
import { SettingsSkeleton } from "#/components/skeletons/settings-skeleton"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "#/components/ui/alert-dialog"
import { Button } from "#/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card"
import { Checkbox } from "#/components/ui/checkbox"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "#/components/ui/empty"
import { Input } from "#/components/ui/input"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "#/components/ui/table"
import { trackEvent } from "#/lib/posthog"

export const Route = createFileRoute("/_authenticated/admin/tenants/$orgId/departments")({
	errorComponent: RouteError,
	component: DepartmentsTab,
})

function DepartmentsTab() {
	const { orgId } = Route.useParams()
	const departments = useQuery(api.stonelog.departments.list, { actingOrgId: orgId })
	const logs = useQuery(api.stonelog.logs.list, { actingOrgId: orgId })
	const createDept = useMutation(api.stonelog.departments.create)
	const removeDept = useMutation(api.stonelog.departments.remove)
	const assignToLog = useMutation(api.stonelog.departments.assignToLog)
	const removeFromLog = useMutation(api.stonelog.departments.removeFromLog)
	const reorderDepts = useMutation(api.stonelog.departments.reorder)

	const [newName, setNewName] = useState("")
	const [orderedIds, setOrderedIds] = useState<Id<"departments">[] | null>(null)

	useEffect(() => {
		if (departments) setOrderedIds(departments.map((d) => d._id))
	}, [departments])

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	if (!departments || !logs || !orderedIds) {
		return <SettingsSkeleton />
	}

	const deptById = new Map(departments.map((d) => [d._id, d]))
	const orderedDepartments = orderedIds
		.map((id) => deptById.get(id))
		.filter((d): d is NonNullable<typeof d> => d !== undefined)

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event
		if (!over || active.id === over.id) return
		const oldIndex = orderedIds.indexOf(active.id as Id<"departments">)
		const newIndex = orderedIds.indexOf(over.id as Id<"departments">)
		if (oldIndex < 0 || newIndex < 0) return
		const previous = orderedIds
		const next = arrayMove(orderedIds, oldIndex, newIndex)
		setOrderedIds(next)
		try {
			await reorderDepts({ orderedIds: next, actingOrgId: orgId })
		} catch (err) {
			Sentry.captureException(err)
			setOrderedIds(previous)
			toast.error(err instanceof Error ? err.message : "Failed to reorder departments")
		}
	}

	const handleAdd = async () => {
		const trimmed = newName.trim()
		if (!trimmed) return
		try {
			const deptId = await createDept({ name: trimmed, actingOrgId: orgId })
			trackEvent("stonelog_department_created", { department_id: deptId, name: trimmed })
			setNewName("")
			toast.success("Department created")
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to create department")
		}
	}

	return (
		<section className="space-y-6">
			<div>
				<h3>Departments</h3>
				<p className="text-sm text-foreground-muted">
					Define the departments (functions) used to assign exceptions, then choose which logs each
					one applies to.
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Add Department</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex items-center gap-2">
						<Input
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
							placeholder="Department name"
							onKeyDown={(e) => e.key === "Enter" && handleAdd()}
							className="max-w-xs"
						/>
						<Button onClick={handleAdd} disabled={!newName.trim()}>
							Add
						</Button>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Departments ({departments.length})</CardTitle>
				</CardHeader>
				<CardContent>
					{orderedDepartments.length === 0 ? (
						<Empty className="border-0 p-6">
							<EmptyHeader>
								<EmptyMedia variant="icon">
									<Building2 />
								</EmptyMedia>
								<EmptyTitle>No departments yet</EmptyTitle>
								<EmptyDescription>Add one above.</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-[40px]" />
									<TableHead>Name</TableHead>
									{logs.map((log) => (
										<TableHead key={log._id} className="text-center">
											{log.name}
										</TableHead>
									))}
									<TableHead className="w-[60px]" />
								</TableRow>
							</TableHeader>
							<TableBody>
								<DndContext
									sensors={sensors}
									collisionDetection={closestCenter}
									onDragEnd={handleDragEnd}
								>
									<SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
										{orderedDepartments.map((dept) => (
											<DepartmentRow
												key={dept._id}
												department={dept}
												logs={logs}
												actingOrgId={orgId}
												onAssign={async (logId) => {
													try {
														await assignToLog({
															logId,
															departmentId: dept._id,
															actingOrgId: orgId,
														})
														toast.success("Department assigned to log")
													} catch (err) {
														Sentry.captureException(err)
														toast.error(
															err instanceof Error ? err.message : "Failed to assign department",
														)
													}
												}}
												onUnassign={async (logId) => {
													try {
														await removeFromLog({
															logId,
															departmentId: dept._id,
															actingOrgId: orgId,
														})
														toast.success("Department removed from log")
													} catch (err) {
														Sentry.captureException(err)
														toast.error(
															err instanceof Error
																? err.message
																: "Failed to remove department from log",
														)
													}
												}}
												onDelete={async () => {
													try {
														await removeDept({ departmentId: dept._id, actingOrgId: orgId })
														trackEvent("stonelog_department_deleted", {
															department_id: dept._id,
														})
														toast.success("Department deleted")
													} catch (err) {
														Sentry.captureException(err)
														toast.error(
															err instanceof Error ? err.message : "Failed to delete department",
														)
													}
												}}
											/>
										))}
									</SortableContext>
								</DndContext>
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</section>
	)
}

function DepartmentRow({
	department,
	logs,
	actingOrgId,
	onAssign,
	onUnassign,
	onDelete,
}: {
	department: { _id: Id<"departments">; name: string }
	logs: Array<{ _id: Id<"logs">; name: string }>
	actingOrgId?: string
	onAssign: (logId: Id<"logs">) => void
	onUnassign: (logId: Id<"logs">) => void
	onDelete: () => void
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: department._id,
	})
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	}

	return (
		<TableRow ref={setNodeRef} style={style}>
			<TableCell className="w-[40px] pr-0">
				<button
					type="button"
					className="cursor-grab touch-none text-foreground-muted hover:text-foreground"
					aria-label="Reorder department"
					{...attributes}
					{...listeners}
				>
					<GripVertical className="size-4" />
				</button>
			</TableCell>
			<TableCell className="font-medium">{department.name}</TableCell>
			{logs.map((log) => (
				<LogAssignmentCell
					key={log._id}
					logId={log._id}
					departmentId={department._id}
					actingOrgId={actingOrgId}
					onAssign={() => onAssign(log._id)}
					onUnassign={() => onUnassign(log._id)}
				/>
			))}
			<TableCell>
				<AlertDialog>
					<AlertDialogTrigger render={<Button variant="destructive" size="icon-xs" />}>
						<Trash2 className="size-3" />
					</AlertDialogTrigger>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Delete Department</AlertDialogTitle>
							<AlertDialogDescription>
								Delete &ldquo;{department.name}&rdquo;? This will remove it from all logs.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</TableCell>
		</TableRow>
	)
}

function LogAssignmentCell({
	logId,
	departmentId,
	actingOrgId,
	onAssign,
	onUnassign,
}: {
	logId: Id<"logs">
	departmentId: Id<"departments">
	actingOrgId?: string
	onAssign: () => void
	onUnassign: () => void
}) {
	const depsForLog = useQuery(api.stonelog.departments.listForLog, { logId, actingOrgId })
	const isAssigned = depsForLog?.some((d) => d._id === departmentId) ?? false

	return (
		<TableCell className="[&:has([role=checkbox])]:pr-6">
			<div className="flex justify-center">
				<Checkbox
					checked={isAssigned}
					onCheckedChange={(checked) => {
						if (checked) onAssign()
						else onUnassign()
					}}
				/>
			</div>
		</TableCell>
	)
}
